// Normalizes any error coming from RTK Query / fetch into a single shape:
//   { status, fieldErrors: { [name]: string }, message: string, raw }
//
// Backend contract (see UniEduBackend/core/exceptions.py):
//   { status: false, status_code, errors: { <field>: [msg, ...], non_field_errors: [...] } }

const NETWORK_FALLBACK =
  "Network error: unable to reach the server. Please check your connection and try again.";
const GENERIC_FALLBACK =
  "Something went wrong. Please try again, or contact support if it keeps happening.";
const RATE_LIMIT_FALLBACK =
  "You're doing that too often. Please wait a moment and try again.";

// Turn DRF's throttle detail ("Request was throttled. Expected available in
// 2302 seconds.") into a friendly wait time, e.g. "about 38 minutes".
function humanizeWait(seconds) {
  if (!seconds || seconds < 1) return "a moment";
  if (seconds < 60) return `about ${Math.ceil(seconds)} seconds`;
  const mins = Math.ceil(seconds / 60);
  if (mins < 60) return `about ${mins} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.round((seconds / 3600) * 10) / 10;
  const whole = Math.floor(hrs);
  return `about ${whole >= 1 ? whole : hrs} hour${whole === 1 ? "" : "s"}`;
}

export function extractRetrySeconds(error) {
  const detail =
    error?.data?.detail ||
    (Array.isArray(error?.data?.errors?.detail) ? error.data.errors.detail[0] : "") ||
    "";
  const match = String(detail).match(/(\d+)\s*seconds?/i);
  return match ? parseInt(match[1], 10) : null;
}

export function rateLimitMessage(error) {
  const seconds = extractRetrySeconds(error);
  if (seconds) {
    return `For your security, we've temporarily paused new attempts. Please try again in ${humanizeWait(seconds)}.`;
  }
  return "For your security, we've temporarily paused new attempts. Please wait a little while and try again.";
}
const AUTH_FALLBACK = "Your session has expired. Please log in again.";
const FORBIDDEN_FALLBACK = "You don't have permission to perform this action.";

export function parseApiError(error) {
  if (!error) {
    return { status: 0, fieldErrors: {}, message: GENERIC_FALLBACK, raw: null };
  }

  // RTK Query network/parse-level failures
  if (error.status === "FETCH_ERROR") {
    return { status: 0, fieldErrors: {}, message: NETWORK_FALLBACK, raw: error };
  }
  if (error.status === "PARSING_ERROR" || error.name === "SyntaxError") {
    return {
      status: error.originalStatus || 500,
      fieldErrors: {},
      message:
        "We received an unexpected response from the server. Please try again shortly.",
      raw: error,
    };
  }
  if (error.status === "TIMEOUT_ERROR") {
    return {
      status: 0,
      fieldErrors: {},
      message: "The request took too long. Please try again.",
      raw: error,
    };
  }

  const status = typeof error.status === "number" ? error.status : 0;
  const data = error.data || {};
  const errorsObj =
    data && typeof data === "object" && data.errors && typeof data.errors === "object"
      ? data.errors
      : null;

  const fieldErrors = {};
  let nonFieldMessages = [];

  if (errorsObj) {
    for (const [key, value] of Object.entries(errorsObj)) {
      const msg = Array.isArray(value) ? value.filter(Boolean).join(" ") : String(value);
      if (key === "non_field_errors" || key === "detail") {
        if (msg) nonFieldMessages.push(msg);
      } else {
        fieldErrors[key] = msg;
      }
    }
  } else if (typeof data === "string" && data) {
    nonFieldMessages.push(data);
  } else if (data && typeof data.detail === "string") {
    nonFieldMessages.push(data.detail);
  }

  let message = nonFieldMessages.join(" ").trim();

  // 429 always gets the friendly, humanized message — never DRF's raw
  // "Request was throttled. Expected available in N seconds." detail.
  if (status === 429) {
    return {
      status,
      fieldErrors,
      message: rateLimitMessage(error),
      retryAfter: extractRetrySeconds(error),
      isRateLimit: true,
      raw: error,
    };
  }

  if (!message) {
    if (status === 401) message = AUTH_FALLBACK;
    else if (status === 403) message = FORBIDDEN_FALLBACK;
    else if (status === 404) message = "Resource not found.";
    else if (Object.keys(fieldErrors).length > 0)
      message = "Please review the highlighted fields and try again.";
    else message = GENERIC_FALLBACK;
  }

  return { status, fieldErrors, message, isRateLimit: false, raw: error };
}
